import { useState } from "react";
import { Heart, Eye, Brain, DoorOpen, RotateCcw } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const TOTEMS = [
  {
    key: "heart",
    name: "The Heart",
    icon: Heart,
    card: "/assets/cards/card-heart.png",
    reading: [
      "You think and decide with your emotions. You often view the glass as half full, approaching life with empathy. There is nothing wrong with how you perceive the world, but consider widening your horizons.",
      "Not every decision or opportunity needs to be led entirely by emotion. Your heart will always have a say, but sometimes it needs to guide from a different seat.",
      "Your vulnerability is your superpower, and it is safe to be open in your choices while still protecting your energy. You can wear your heart on your chest without feeling guarded.",
      "If past rejection or hurt has caused you to build walls, remember that softness and self protection can coexist.",
    ],
  },
  {
    key: "eye",
    name: "The Eye",
    icon: Eye,
    card: "/assets/cards/card-eye.png",
    reading: [
      "You see the bigger picture. Your perspective is invaluable when others are lost in the details or unable to see the full scope of a situation.",
      "You excel at stepping back from the immediate noise to understand how all the pieces of the puzzle fit together.",
      "Because your vision is so expansive, grounding yourself in the present moment is essential. Sometimes you can get so caught up looking far ahead into the future, or reflecting on the past, that you miss living in the in between.",
      "Remember to slow down, stay present, and appreciate where you are right now.",
    ],
  },
  {
    key: "head",
    name: "The Head",
    icon: Brain,
    card: "/assets/cards/card-head.png",
    reading: [
      "You lead with logic and intellect. The ego often gets a bad reputation because people associate it purely with self centeredness, but that is not always the case.",
      "Logically, you weigh objective facts over emotion when making decisions because you value clarity, structure, and precision.",
      "However, relying strictly on logic can narrow your vision. When you focus solely on cold facts, you risk missing the broader emotional reality around you.",
      "Balancing your analytical mind with the wisdom of your heart allows you to make decisions that are both smart and deeply fulfilling.",
    ],
  },
  {
    key: "door",
    name: "The Door",
    icon: DoorOpen,
    card: "/assets/cards/card-door.webp",
    reading: [
      "Your eyes land on the door, a symbol of pure possibility, no one knows what lies beyond it until they choose to open it. Once you make that decision, you naturally engage your mind, body, and spirit.",
      "You see the other side of the pieces, forecasting the results and anticipating the game ahead. You are a forward thinker who seeks complete certainty before taking a step.",
      "However, be mindful not to move on too quickly. Seeing the outline of the pieces may make you feel like you have already seen everything there is to experience.",
      "Allow yourself to journey through each perspective thoroughly before walking out that door. Grounding yourself in the present will keep you from constantly living on the go.",
    ],
  },
];

export function ConsequentialGame() {
  const [chosen, setChosen] = useState(null);
  const active = TOTEMS.find((t) => t.key === chosen);

  return (
    <section
      className="bg-ink text-white border border-ink"
      data-testid="consequential-game"
    >
      <div className="mx-auto max-w-6xl px-6 md:px-10 py-16 md:py-24">
        <p className="text-[11px] uppercase tracking-[0.3em] text-flame">An interactive reading</p>
        <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight">
          Welcome to the Consequential Game
        </h2>
        <p className="mt-5 max-w-2xl text-neutral-300 leading-relaxed">
          Each piece represents a part of you. Whichever totem your eyes land on first holds a message
          for your journey.
        </p>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3" role="group" aria-label="Choose your totem">
          {TOTEMS.map((t) => {
            const Icon = t.icon;
            const isActive = chosen === t.key;
            return (
              <button
                key={t.key}
                type="button"
                data-testid={`totem-${t.key}`}
                aria-pressed={isActive}
                onClick={() => setChosen(isActive ? null : t.key)}
                className={`border p-6 text-center transition-colors ${
                  isActive
                    ? "bg-flame border-flame text-white"
                    : "border-white/20 text-neutral-200 hover:border-flame hover:text-flame"
                }`}
              >
                <Icon size={28} className="mx-auto" strokeWidth={1.5} />
                <p className="mt-3 text-sm tracking-wide">{t.name}</p>
              </button>
            );
          })}
        </div>

        <AnimatePresence mode="wait">
          {active && (
            <motion.div
              key={active.key}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="mt-12 grid gap-10 md:grid-cols-[280px_1fr] items-start"
              data-testid="totem-reading"
            >
              <img
                src={active.card}
                alt={`${active.name} card from the Consequential Game exhibition`}
                className="w-full max-w-[280px] border border-white/15"
              />
              <div>
                <p className="text-[11px] uppercase tracking-[0.3em] text-flame">
                  If your eyes stopped at {active.name.replace("The ", "the ")} first
                </p>
                <h3 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight">{active.name}</h3>
                <div className="mt-6 space-y-5 max-w-2xl">
                  {active.reading.map((p, i) => (
                    <p key={i} className="text-neutral-300 leading-relaxed">
                      {p}
                    </p>
                  ))}
                </div>
                <button
                  type="button"
                  data-testid="totem-reset"
                  onClick={() => setChosen(null)}
                  className="mt-8 inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-flame transition-colors"
                >
                  <RotateCcw size={14} /> Choose again
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
